import { useState, useEffect, useRef, useCallback } from 'react';
import { Music, Search, X, Play, Pause, ChevronRight } from 'lucide-react';
import { useThemeClasses } from '../hooks/useTheme';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GENRE_TABS = ['All', 'Hip-Hop', 'R&B', 'Afrobeats', 'Soul', 'Gospel', 'Jazz', 'Neo-Soul', 'Trap', 'Drill', 'Lo-Fi'];

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MusicPicker({ open, onClose, onSelect, selectedTrack }) {
  const { isDark, textClass, textMutedClass, borderClass } = useThemeClasses();

  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('All');
  const [playingId, setPlayingId] = useState(null);
  const [playProgress, setPlayProgress] = useState(0);
  const [pendingTrack, setPendingTrack] = useState(null);
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const PREVIEW_SECONDS = 15;

  const fetchTracks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (genre !== 'All') params.genre = genre.toLowerCase();
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/api/music/tracks`, { params });
      setTracks(res.data.tracks || []);
    } catch (e) {
      console.error('Music fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [genre, search]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchTracks, 300);
    return () => clearTimeout(t);
  }, [open, fetchTracks]);

  // Cleanup audio on close
  useEffect(() => {
    if (!open) stopAudio();
  }, [open]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    clearInterval(progressIntervalRef.current);
    setPlayingId(null);
    setPlayProgress(0);
  };

  const handlePlay = (track) => {
    if (playingId === track.track_id) {
      stopAudio();
      return;
    }

    stopAudio();

    // Log play
    axios.post(`${API}/api/music/tracks/${track.track_id}/play`).catch(() => {});

    const audio = new Audio(track.file_url);
    audioRef.current = audio;
    setPlayingId(track.track_id);
    setPlayProgress(0);

    audio.play().catch(() => toast.error('Could not play preview'));

    progressIntervalRef.current = setInterval(() => {
      if (!audioRef.current) return;
      const pct = (audioRef.current.currentTime / PREVIEW_SECONDS) * 100;
      setPlayProgress(Math.min(pct, 100));

      if (audioRef.current.currentTime >= PREVIEW_SECONDS) {
        stopAudio();
      }
    }, 200);

    audio.onended = () => stopAudio();
  };

  const handleSelect = () => {
    if (!pendingTrack) return;
    onSelect(pendingTrack);
    stopAudio();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" onClick={(e) => { if (e.target === e.currentTarget) { stopAudio(); onClose(); } }}>
      {/* Backdrop */}
      <div className="flex-1" />

      {/* Sheet */}
      <div className={cn(
        "w-full rounded-t-2xl flex flex-col",
        isDark ? 'bg-zinc-950 border-t border-white/10' : 'bg-white border-t border-gray-200'
      )} style={{ maxHeight: '75vh' }}>

        {/* Header */}
        <div className={cn("flex items-center justify-between px-4 pt-4 pb-3 border-b shrink-0", borderClass)}>
          <div className="flex items-center gap-2">
            <Music className="h-4 w-4 text-amber-500" />
            <span className={cn("text-sm font-display tracking-wider", textClass)}>Music Library</span>
          </div>
          <button onClick={() => { stopAudio(); onClose(); }} className={cn("p-1.5", textMutedClass)}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2 shrink-0">
          <div className={cn("flex items-center gap-2 px-3 py-2 border rounded-lg", isDark ? 'border-white/10 bg-white/5' : 'border-gray-200')}>
            <Search className={cn("h-3.5 w-3.5 shrink-0", textMutedClass)} />
            <input
              type="text"
              placeholder="Search by title or artist..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={cn("flex-1 text-xs bg-transparent outline-none", textClass)}
            />
            {search && (
              <button onClick={() => setSearch('')} className={textMutedClass}><X className="h-3 w-3" /></button>
            )}
          </div>
        </div>

        {/* Genre tabs */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto hide-scrollbar shrink-0">
          {GENRE_TABS.map(g => (
            <button
              key={g}
              onClick={() => setGenre(g)}
              className={cn(
                "px-3 py-1.5 text-[10px] font-display tracking-wider uppercase whitespace-nowrap rounded-full shrink-0 transition-colors",
                genre === g
                  ? 'bg-amber-500 text-black'
                  : isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-900'
              )}
            >
              {g}
            </button>
          ))}
        </div>

        {/* Track list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 hide-scrollbar">
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : tracks.length === 0 ? (
            <div className="py-12 text-center">
              <Music className={cn("h-8 w-8 mx-auto mb-3", textMutedClass)} />
              <p className={cn("text-sm", textMutedClass)}>No tracks yet</p>
              <p className={cn("text-xs mt-1", textMutedClass)}>
                Artists can submit at{' '}
                <a href="/music/submit" className="text-amber-500 hover:underline">blvx.social/music/submit</a>
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {tracks.map(track => {
                const isPlaying = playingId === track.track_id;
                const isPending = pendingTrack?.track_id === track.track_id;
                const isCurrent = selectedTrack?.track_id === track.track_id;

                return (
                  <div
                    key={track.track_id}
                    onClick={() => setPendingTrack(isPending ? null : track)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all",
                      isPending || isCurrent
                        ? isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'
                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                    )}
                  >
                    {/* Cover art */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-amber-500/10 flex items-center justify-center">
                      {track.cover_art_url ? (
                        <img src={track.cover_art_url} alt={track.title} className="w-full h-full object-cover" />
                      ) : (
                        <Music className="h-4 w-4 text-amber-500" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-sm font-medium truncate", textClass)}>{track.title}</p>
                      <p className={cn("text-xs truncate", textMutedClass)}>{track.stage_name}</p>
                      {/* Genre tags */}
                      <div className="flex gap-1 mt-0.5">
                        {(track.genre_tags || []).slice(0, 2).map(g => (
                          <span key={g} className={cn("text-[9px] font-display tracking-wider uppercase", textMutedClass)}>
                            {g}
                          </span>
                        ))}
                      </div>
                      {/* Progress bar when playing */}
                      {isPlaying && (
                        <div className={cn("mt-1 h-0.5 rounded-full overflow-hidden", isDark ? 'bg-white/10' : 'bg-gray-200')}>
                          <div
                            className="h-full bg-amber-500 transition-all duration-200"
                            style={{ width: `${playProgress}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Duration + play button */}
                    <div className="flex items-center gap-2 shrink-0">
                      {track.duration_seconds && (
                        <span className={cn("text-[10px]", textMutedClass)}>
                          {formatDuration(track.duration_seconds)}
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePlay(track); }}
                        className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center transition-colors",
                          isPlaying
                            ? 'bg-amber-500 text-black'
                            : isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                      >
                        {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Use This Track CTA */}
        {pendingTrack && (
          <div className={cn("px-4 pb-6 pt-3 border-t shrink-0", borderClass)}>
            <div className={cn("flex items-center gap-3 p-3 mb-3 rounded-xl", isDark ? 'bg-white/5' : 'bg-gray-50')}>
              <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 bg-amber-500/10 flex items-center justify-center">
                {pendingTrack.cover_art_url ? (
                  <img src={pendingTrack.cover_art_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Music className="h-3 w-3 text-amber-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn("text-xs font-medium truncate", textClass)}>{pendingTrack.title}</p>
                <p className={cn("text-[10px] truncate", textMutedClass)}>{pendingTrack.stage_name}</p>
              </div>
              <ChevronRight className={cn("h-4 w-4", textMutedClass)} />
            </div>
            <button
              onClick={handleSelect}
              className="w-full py-3 bg-amber-500 text-black text-sm font-display tracking-widest uppercase hover:bg-amber-400 transition-colors"
            >
              Use This Track
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact music card shown attached to a post.
 * Pass `track` object and optional `playing`/`onPlay` for playback.
 */
export function AttachedTrackCard({ track, onRemove, isDark }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const PREVIEW_SECONDS = 15;

  const toggle = () => {
    if (playing) {
      audioRef.current?.pause();
      clearInterval(intervalRef.current);
      setPlaying(false);
    } else {
      const audio = audioRef.current || new Audio(track.file_url);
      audioRef.current = audio;
      audio.play().catch(() => {});
      setPlaying(true);
      intervalRef.current = setInterval(() => {
        if (audio.currentTime >= PREVIEW_SECONDS) {
          audio.pause();
          audio.currentTime = 0;
          clearInterval(intervalRef.current);
          setPlaying(false);
        }
      }, 200);
      audio.onended = () => { clearInterval(intervalRef.current); setPlaying(false); };
    }
  };

  return (
    <div className={cn(
      "flex items-center gap-3 px-3 py-2 border rounded-xl",
      isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'
    )}>
      {/* Waveform animation when playing */}
      <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 bg-amber-500/10 flex items-center justify-center relative">
        {track.cover_art_url ? (
          <img src={track.cover_art_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <Music className="h-3.5 w-3.5 text-amber-500" />
        )}
        {playing && (
          <div className="absolute inset-0 flex items-end justify-center gap-0.5 pb-1 bg-black/40">
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="w-0.5 bg-amber-500 animate-pulse rounded-full"
                style={{ height: `${8 + i * 3}px`, animationDelay: `${i * 80}ms` }}
              />
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn("text-xs font-medium truncate", isDark ? 'text-white' : 'text-gray-900')}>{track.title}</p>
        <p className={cn("text-[10px] truncate", isDark ? 'text-white/40' : 'text-gray-400')}>{track.stage_name}</p>
      </div>
      <button
        onClick={toggle}
        className={cn(
          "w-6 h-6 rounded-full flex items-center justify-center shrink-0",
          playing ? 'bg-amber-500 text-black' : isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
        )}
      >
        {playing ? <Pause className="h-2.5 w-2.5" /> : <Play className="h-2.5 w-2.5" />}
      </button>
      {onRemove && (
        <button onClick={onRemove} className={cn("ml-1", isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-300 hover:text-gray-600')}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
